import log from 'loglevel';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  checkForValue,
  fromUserRoleToUrl,
  NotificationMessages,
  ReportMessages,
  URLS,
} from '../../../utils';
import { useAxios, useShowNotification } from '../../../utils/hooks';
import { InputSelectListOption, PaginationData } from '../../../utils/types';
import { CompanyResponse } from '../../../utils/types/company';
import { AltPlainTruckData } from '../../../utils/types/truck';
import useStore from '../../../zustand';
import { TrucksReportsResponse } from '../types';

type Props = {
  paginationData: PaginationData;
};

const useTrucksReports = ({ paginationData }: Props) => {
  const API_URL_TRUCKS_REPORTS = '/reports/truck';
  const TRANSPORT_URL =
    '/companies?filters[type][$eq]=Transportista&filters[business_name][$containsi]=';

  const API_URL_TRUCK_REPORT_GENERATION_PDF = '/reports/format/pdf/truck',
    API_URL_TRUCK_REPORT_GENERATION_XLSX = '/reports/format/xlsx/truck';

  const paginationParams = `?page=${paginationData.current}&per_page=${paginationData.pageSize}`;

  const axios = useAxios();
  const navigate = useNavigate();

  const [userRole] = useStore((state) => [state.userRole]);
  const transformedUserRole = fromUserRoleToUrl(userRole);

  const { showNotification, showErrorNotification } = useShowNotification();

  const [data, setData] = useState<AltPlainTruckData[]>([]);
  const [totalResults, setTotalResults] = useState(0);

  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  //----- Filters
  // APIs
  const API_URL_FILTERS = {
    isInsurancePolicyExpired: '&insurance_policy_expired=true',
    licenseOrBrand: '&truck_brand_or_license_plate=',
    transport: '&carrier_company_id=',
  };
  const API_URL_SORTERS = {
    sortBy: '&sort=',
    orderBy: '&order=',
  };

  // Filter Queries
  const [transportQuery, setTransportQuery] = useState('');

  // Options States
  const [transportOptions, setTransportOptions] = useState<
    InputSelectListOption[]
  >([]);

  // Controls
  const [filterIsInsurancePolicyExpired, setFilterIsInsurancePolicyExpired] =
    useState(false);
  const [filterLicenseOrBrand, setFilterLicenseOrBrand] = useState('');
  const [filterTransport, setFilterTransport] =
    useState<InputSelectListOption | null>({} as InputSelectListOption);
  const [sorterSortBy, setSorterSortBy] = useState('');
  const [sorterOrderBy, setSorterOrderBy] = useState('');

  // Fetch Options
  // Transport
  const getTransportOptions = () => {
    const token = sessionStorage.getItem('token');
    if (!token) log.error('No Token provided');

    axios
      .get<CompanyResponse>(`${TRANSPORT_URL}${transportQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const rawResponse = response.data.data;

        const results: InputSelectListOption[] =
          rawResponse.length > 0
            ? rawResponse.map((item) => {
                return { label: item.attributes.business_name, value: item.id };
              })
            : [];

        setTransportOptions(results);
      })
      .catch((error) => {
        log.error(error);
      });
  };
  useEffect(() => {
    getTransportOptions();
  }, [transportQuery]);

  // Build final URL

  const finalURL = () => {
    const INSURANCE_POLICY_EXPIRED_URL =
      filterIsInsurancePolicyExpired &&
      API_URL_FILTERS.isInsurancePolicyExpired;
    const LICENSE_OR_BRAND_URL =
      filterLicenseOrBrand &&
      API_URL_FILTERS.licenseOrBrand + filterLicenseOrBrand;
    const TRANSPORT_URL =
      filterTransport?.value &&
      API_URL_FILTERS.transport + filterTransport?.value;
    const SORT_BY_URL = sorterSortBy && API_URL_SORTERS.sortBy + sorterSortBy;
    const ORDER_BY_URL =
      sorterOrderBy && API_URL_SORTERS.orderBy + sorterOrderBy;

    return (
      API_URL_TRUCKS_REPORTS +
      paginationParams +
      checkForValue(SORT_BY_URL) +
      checkForValue(ORDER_BY_URL) +
      checkForValue(INSURANCE_POLICY_EXPIRED_URL) +
      checkForValue(LICENSE_OR_BRAND_URL) +
      checkForValue(TRANSPORT_URL)
    );
  };

  const tableFilters = {
    isInsurancePolicyExpired: setFilterIsInsurancePolicyExpired,
    licenseOrBrand: setFilterLicenseOrBrand,
    transport: setFilterTransport,
    sortBy: setSorterSortBy,
    orderBy: setSorterOrderBy,
  };

  const getTrucksReports = async () => {
    setIsLoading(true);

    const token = sessionStorage.getItem('token');
    if (!token) {
      throw new Error('No token provided');
    }

    const result = await axios.get<TrucksReportsResponse>(finalURL(), {
      headers: { Authorization: `Bearer ${token}` },
    });

    setData(result.data.trucks);
    setTotalResults(result.data.trucks_count);
    setIsLoading(false);
  };
  useEffect(() => {
    getTrucksReports().catch((error) => {
      log.error(error);
      setHasError(true);
      setIsLoading(false);
    });
  }, [
    paginationData.current,
    filterIsInsurancePolicyExpired,
    filterLicenseOrBrand,
    filterTransport,
    sorterSortBy,
    sorterOrderBy,
  ]);

  // Reports Generation
  const buildReportGenerationURL = (apiToUse: string) => {
    const INSURANCE_POLICY_EXPIRED_URL =
      filterIsInsurancePolicyExpired &&
      API_URL_FILTERS.isInsurancePolicyExpired;
    const LICENSE_OR_BRAND_URL =
      filterLicenseOrBrand &&
      API_URL_FILTERS.licenseOrBrand + filterLicenseOrBrand;
    const TRANSPORT_URL =
      filterTransport?.value &&
      API_URL_FILTERS.transport + filterTransport?.value;
    const SORT_BY_URL = sorterSortBy && API_URL_SORTERS.sortBy + sorterSortBy;
    const ORDER_BY_URL =
      sorterOrderBy && API_URL_SORTERS.orderBy + sorterOrderBy;

    const result =
      apiToUse +
      checkForValue(SORT_BY_URL) +
      checkForValue(ORDER_BY_URL) +
      checkForValue(INSURANCE_POLICY_EXPIRED_URL) +
      checkForValue(LICENSE_OR_BRAND_URL) +
      checkForValue(TRANSPORT_URL);
    return result.replace('&', '?');
  };

  const generateReport = async (type: 'pdf' | 'xlxs') => {
    const token = sessionStorage.getItem('token');

    const urlToUse =
      type === 'pdf'
        ? API_URL_TRUCK_REPORT_GENERATION_PDF
        : API_URL_TRUCK_REPORT_GENERATION_XLSX;

    setIsLoading(true);

    try {
      if (!token) {
        throw new Error('No Token provided');
      }

      await axios.get(buildReportGenerationURL(urlToUse), {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIsLoading(false);
      showNotification({
        message: ReportMessages.SUCCESS,
      });
      navigate(`/${transformedUserRole}${URLS.filesTruck}`);
    } catch (error) {
      log.error(error);
      showErrorNotification({
        message: NotificationMessages.GENERIC_ERROR,
      });
      setHasError(true);
    }
  };

  const generateReportPDF = () => {
    generateReport('pdf');
  };

  const generateReportXLXS = () => {
    generateReport('xlxs');
  };

  return {
    data,
    hasError,
    isLoading,
    setTransportQuery,
    tableFilters,
    totalResults,
    transportOptions,
    generateReportPDF,
    generateReportXLXS,
  };
};

export default useTrucksReports;
